import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { KiteProgressIndicator } from '@kite/react-kite';
import { UserCapabilityName } from '../types/types.generated';
import { IAuthUserContext } from '../types/authUser';
import { hasCapability, hasService } from './utils/authUserUtils';
import { useAuthUserFetcher } from './hooks/useAuthUserFetcher';
import { useAuthSession } from '../hooks/useAuthSession';
import authUserContextDefault from './utils/authUserDefault';
import {
  useAllOrganizationsQuery,
  useAllUsersQuery,
} from '../hooks/useRequest.generated';
import { useFeatureToggles } from '../hooks/useFeatureToggles';
import { FeatureToggleName } from '../config/featureToggles/featureToggles.types';

export const AuthUserContext = createContext<IAuthUserContext>(
  authUserContextDefault
);

export const AuthUserContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const { isAuthenticated, isAuthenticating, signOut } = useAuthSession();

  const { loadAuthUser, clearAuthUser, authUser } = useAuthUserFetcher({
    loadUserOnSuccess: useCallback(() => {
      setIsLoading(false);
    }, []),
    loadUserOnFailure: useCallback(() => {
      clearAuthUser();
      setIsError(true);
    }, []),
  });

  const { isFeatureToggledOn } = useFeatureToggles();
  const prefetchData = isFeatureToggledOn(FeatureToggleName.PrefetchData);

  // Once Auth User is loaded, start pre-loading data in the background.
  useAllOrganizationsQuery({}, { enabled: !!authUser && prefetchData });
  useAllUsersQuery(
    {
      isInternalUser: false,
      limit: 20,
      offset: 0,
      organizationIds: null,
      sortBy: 'asc(lastName)',
      status: null,
    },
    { enabled: !!authUser && prefetchData }
  );

  useEffect(() => {
    // Wait for Auth Session to finish loading as authUSer depends on a valid Auth Session
    if (isAuthenticating) {
      setIsError(false);
      return;
    }

    if (isAuthenticated && !isError) {
      // Load the auth user object when we have everything
      loadAuthUser();
      return;
    }

    // Handle Auth errors on Browser Refresh
    if (isLoading && isAuthenticated) {
      if (isError) {
        signOut(true).then(
          () => {
            // do nothing
          },
          () => {
            // do nothing
          }
        );
      }

      return;
    }

    // No auth user to load, make sure everything is cleared out.
    clearAuthUser();
    setIsLoading(false);
  }, [
    clearAuthUser,
    isAuthenticated,
    isAuthenticating,
    isError,
    isLoading,
    loadAuthUser,
    authUser,
    signOut,
  ]);

  /**
   * Check whether the auth user has a capability
   * @param capabilities Single or array of capabilities to check
   */
  const userHasCapability = useCallback(
    (capabilities: UserCapabilityName | UserCapabilityName[]) => {
      const userCapabilities =
        authUser?.capabilities
          ?.map((item) => item?.name || '')
          .filter((name) => !!name)
          .map((name) => name as UserCapabilityName) || [];
      return hasCapability(capabilities, userCapabilities);
    },
    [authUser]
  );

  /**
   * Check whether the auth user has given service with appropriate capabilities
   * @param service Name of service to cross check
   * @param capabilities Array of capabilities to check
   */
  const userHasService = useCallback(
    (service: string, capabilities: UserCapabilityName[]) => {
      return hasService(
        service,
        authUser?.services || [],
        authUser?.capabilities.filter((cap) =>
          capabilities.includes((cap?.name as UserCapabilityName) || '')
        ) || []
      );
    },
    [authUser]
  );

  return (
    <AuthUserContext.Provider
      value={useMemo(
        () => ({
          authUser,
          isError,
          isAuthenticated,
          hasCapability: userHasCapability,
          hasService: userHasService,
        }),
        [authUser, isError, isAuthenticated, userHasCapability, userHasService]
      )}
    >
      {!isLoading && children}
      {isLoading && (
        <div className="display-loader">
          <KiteProgressIndicator id="auth-user-loader" />
        </div>
      )}
    </AuthUserContext.Provider>
  );
};
